// Node Libraries
import React, { useEffect, useRef, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useDispatch, useSelector } from 'react-redux';
import { TertiaryButton } from 'swp-react';
// Redux
import {
	selectGoogleAccessToken,
	selectGoogleRefreshToken,
	setGoogleAccessToken,
	setGoogleRefreshToken,
	setIsGoogleFetchingToken,
	selectIsGoogleFetchingToken,
} from '@/slices/googleApiSlice';
// Helper Functions
import { fetchCall } from '../../utils/fetchUtils';
import { getCookie, setCookie } from '@/utils/cookieUtils';
// React Components
import FormattedMessage from '../FormattedMessage';
// Styling
import GooglePhotosStyle from './GooglePhotosButton.module.scss';
import {
	ansiReset,
	pT,
	rT,
	gT,
	buB,
	wT,
	yB,
	bT,
	yT,
	buT,
} from '@/utils/logStyling';
import { logToAi } from '@/utils/logging';

const GooglePhotosButton = (props) => {
	// #region State and Hooks
	const router = useRouter();
	const dispatch = useDispatch();
	const isGoogleFetchingToken = useSelector(selectIsGoogleFetchingToken);
	const googleAccessToken = useSelector(selectGoogleAccessToken);
	const googleRefreshToken = useSelector(selectGoogleRefreshToken);

	/**
	 * Tracks the state we're in while waiting for the authCode cookie from the google auth popup.
	 * * `"off"` or `""`: The popup was never opened so we're not waiting for a cookie from there
	 * * `"baking"`: The popup is opened!  Gotta wait for the user to finish with it
	 * * `"done"`: We got the cookie from the popup!
	 * @type {ReturnType<typeof useState<('' | 'off' | 'baking' | 'done')>>}
	 */
	const [isCookieBaking, setIsCookieBaking] = useState('off');
	const [isLoading, setIsLoading] = useState(true);
	const [redirectUrl, setRedirectUrl] = useState('');
	const [authCode, setAuthCode] = useState('');
	const [cookieGrabAttemptsRemaining, setCookieGrabAttemptsRemaining] =
		useState(150); // Give them 5 minutes to authenticate

	const searchParams = useSearchParams();

	let jwt = searchParams.get('jwt');
	let authCodeFromParams = searchParams.get('code');
	let stateSearchParam = searchParams.get('state');

	const anchorRef = useRef(null);
	//#endregion

	//#region Initialization
	useEffect(() => {
		getAuthUrl();

		// Make sure these are set before we do anything major on the site.
		jwt = searchParams.get('jwt');
		authCodeFromParams = searchParams.get('code');
		stateSearchParam = searchParams.get('state');

		if (authCodeFromParams) {
			setAuthCode(authCodeFromParams);
		}

		console.debug(
			`${pT}GooglePhotosButton mounted${ansiReset}\n` +
				`state param: ${stateSearchParam}\n` +
				`isGoogleFetchingToken: ${isGoogleFetchingToken}\n` +
				`googleAccessToken: ${googleAccessToken}\n` +
				`googleRefreshToken: ${googleRefreshToken}\n` +
				`authCode: ${authCode}`
		);

		setIsLoading(false);
	}, []);

	useEffect(() => {
		// If this page has a search param called `state` that has a value of `opened_from_popup`,
		// that means this came from a google auth popup.
		// We should use the code to get the refresh token variable and store it in a cookie for the main site to obtain.
		// Then we can close the popup.
		if (stateSearchParam !== 'opened_from_popup') {
			console.debug(
				`${pT}Page was ${rT}NOT${pT} opened from a popup.${ansiReset}`
			);
			return;
		}
		if (!authCodeFromParams) {
			console.error(
				"The page was opened from a popup, yet that popup didn't have the auth code we needed."
			);
		} else {
			console.debug(
				`${pT}Setting authCode cookie to ${buB}${wT}${authCodeFromParams}${ansiReset}`
			);
			setCookie('authCode', authCodeFromParams, 20); // A short-lived cookie to pass data from the popup to the main page.
		}
		close();
	}, [stateSearchParam, authCodeFromParams]);
	//#endregion

	//#region Helper Functions
	/**
	 * Opens a small portrait popup window or new tab with the given URL in the center of the users screen.
	 * @param {string} url The URL the popup will take the user to
	 * @param {boolean} isNewTab If true, opens a new tab instead of a popup window
	 * @returns {WindowProxy | null} A `WindowProxy` reference to the popup window if the popup successfully opened, or `null` otherwise
	 */
	function openPopup(url, isNewTab) {
		const width = 500;
		const height = 600;
		const left = (window.innerWidth - width) / 2;
		const top = (window.innerHeight - height) / 2;

		const popup = window.open(
			url + '&state=opened_from_popup',
			isNewTab ? '_blank' : '',
			`width=${width},height=${height},top=${top},left=${left},scrollbars=yes${
				isNewTab ? '' : ',popup'
			}`
		);

		return popup;
	}

	/**
	 * Checks to see if `getCookie('authCode');` returns a value every two seconds.
	 * Stops checking once a cookie is detected.
	 * -----
	 * **!!!IMPORTANT!!!**
	 *
	 * IF YOU WANT THIS FUNCTION TO WORK, THE DOMAIN OF THE COOKIE MUST BE THE SAME AS THE DOMAIN OF THIS PAGE.
	 * Assuming you set the cookie from the google auth popup/tab, that means the redirect URL you gave the Google Auth popup/tab must have the same domain as this URL.
	 * @param {WindowProxy | null | undefined} popup A proxy to the popup that we can use to check if it's closed
	 */
	function checkForAuthCodeCookie(popup) {
		if (isCookieBaking === 'baking') {
			// We're already checking? Just return.
			return;
		}
		setIsCookieBaking('baking');
		const checkForCookieTimer = setInterval(() => {
			const tempAuthCodeFromCookie = getCookie('authCode');

			setCookieGrabAttemptsRemaining((prev) => {
				// We do this in setCookieGrabAttemptsRemaining because it will have the right value for the cookie value.
				if (prev % 5 === 0 || prev <= 10 || tempAuthCodeFromCookie) {
					console.log(
						`The cookie is ${
							tempAuthCodeFromCookie
								? `${gT}READY${ansiReset}!`
								: `${rT}STILL BAKING${ansiReset} (${prev})...`
						}`
					);
				}
				if (popup?.closed && prev > 5) {
					console.debug(
						`${yT}We detected that the popup was closed.` +
							` You have ${buT}5${yT} attempts to get the cookie` +
							` before we stop and you must authenticate again.${ansiReset}`
					);
					return 5;
				}
				if (prev <= 0) {
					console.debug(
						`${rT}You have run out of time to authenticate.` +
							` ${gT}Please, click the "Add from Google Photos" button and try again.${ansiReset}`
					);
					setIsCookieBaking('off');
					clearInterval(checkForCookieTimer);
					return 150; // Reset grab attempts
				}
				return prev - 1;
			});

			if (tempAuthCodeFromCookie) {
				setAuthCode(tempAuthCodeFromCookie);
				console.log(
					`${pT}Stopping the check because we found an authCode` +
						` cookie with a value of ${buB}${wT}${tempAuthCodeFromCookie}${ansiReset}`
				);
				setIsCookieBaking('done');
				clearInterval(checkForCookieTimer);
			}
		}, 2000);
	}
	//#endregion

	//#region Auth API Requests
	async function goToGooglePhotos() {
		logToAi(
			{
				message: `GooglePhotosButton pressed!`,
				id: 'ADD_GOOGLE_BUTTON_PRESS',
				location: 'GooglePhotosButton.jsx:goToGooglePhotos',
			},
			'debug'
		);
		if (googleAccessToken) {
			router.push('/google');
		} else if (googleRefreshToken || authCode) {
			await refreshGoogleAccessToken();
			router.push('/google');
		} else {
			// Can't use this function below because using window.open() in a chained function doesn't work on mobile
			// openGoogleAuthPage();
			console.log(
				`${pT}The URL we plan to open to get the auth code is: ${buB}${wT}${
					redirectUrl + '&state=opened_from_popup'
				}${ansiReset}`
			);
			/** @type {WindowProxy} A reference to the window we just opened */
			const popup = window.open(
				redirectUrl + '&state=opened_from_popup',
				'',
				`width=500,height=600,top=${
					(window.innerHeight - 600) / 2
				},left=${(window.innerWidth - 500) / 2},scrollbars=yes`
			);
			checkForAuthCodeCookie(popup);
		}
	}
	// Think of this as an extension of the last part of the `goToGooglePhotos` function
	useEffect(() => {
		async function waitForGoogleTokens() {
			if (isCookieBaking === 'done' && authCode) {
				await refreshGoogleAccessToken();
				router.push('/google');
			}
		}
		waitForGoogleTokens();
	}, [isCookieBaking, authCode]);

	/**
	 * Opens the Google Auth Page for our app so we can get an Auth Code
	 *
	 * The Google Auth Page will attempt to open in a variety of ways to support multiple browsers on multiple devices.
	 * Below is the order of priority
	 * 1. Open a popup using `window.open();`
	 * 2. Open a new tab using `window.open(url, "_blank")`
	 * 3. Open a new tab by simulating a click event on a hidden `<a/>` element
	 * 4. Redirect the user to the page using `window.location.assign();`
	 *
	 * Unfortunately, we must directly call `window.open()` from the `onClick()` function of a button, so this function is just for reference.
	 */
	async function openGoogleAuthPage() {
		const authUrl = await getAuthUrl();
		if (!authCode && !googleRefreshToken && !googleAccessToken) {
			// Try a popup!
			const popup = openPopup(authUrl + '&state=opened_from_popup');
			if (!popup) {
				console.warn(
					"Couldn't open a popup for the user.  Let's try a new tab."
				);
				// Try a new tab!
				const popup = openPopup(
					authUrl + '&state=opened_from_popup',
					true
				);
				if (!popup) {
					console.warn(
						"Couldn't open a new tab for the user either?" +
							" Let's try a fake click on the hidden anchor element to open a new tab."
					);
					// ALTERNATE WAY TO OPEN A NEW TAB!  Force a click on a hidden anchor element.
					if (anchorRef.current) {
						anchorRef.current.click();
						setTimeout(() => {
							console.debug(
								'Waiting 50ms for the user to switch tabs'
							);
						}, 50);
					}
					if (!document.hidden) {
						// Hmm... couldn't open a new tab with the anchor either :(
						console.warn(
							"Couldn't open the anchor for the user either???  I give up, just do a redirect."
						);
						window.location.assign(
							authUrl + '&state=opened_from_redirect'
						);
						return;
					}
				}
			}
		}
	}

	/**
	 * Gets the URL used by the user to authenticate our app and assigns it to the `redirectUrl` state
	 */
	async function getAuthUrl() {
		return await fetchCall(
			`/api/google/auth?windowLocation=${window.location.origin}`,
			'GET',
			null
		)
			.then((res) => {
				if (typeof res !== 'string') {
					console.log(
						`${yB}${bT}The /api/google/auth API request returned something that wasn't a string.${ansiReset}`,
						res
					);
				}
				setRedirectUrl(res);
				return res;
			})
			.catch((err) => {
				setRedirectUrl('');
				logToAi(
					{
						message: `Unexpected error with /api/google/auth API request.`,
						id: 'API_GOOGLE_AUTH_ERROR',
						location: 'GooglePhotosButton.jsx:getAuthUrl',
						data: {
							error: err,
						},
					},
					'error'
				);
			});
	}

	/**
	 * Gets the `access_token` that we'll use to fetch this user's photos.
	 * This can be done in one of two ways in order of precedence:
	 * 1. Request a new `access_token` using the `refresh_token`
	 * 2. Request a new `access_token` and `refresh_token` using the `authCode` in the state.
	 *
	 * @returns {boolean} `true` if the refresh completed successfully, otherwise `false`
	 */
	async function refreshGoogleAccessToken() {
		// Early bail check!  Are we already getting an auth code?
		if (isGoogleFetchingToken) {
			console.warn(
				'Tried to run refreshGoogleAccessToken but' +
					' this call is already running.'
			);
			return false;
		}
		dispatch(setIsGoogleFetchingToken(true));
		// Check if the user has a refresh token we can use to get a new auth token.
		// Refresh tokens are always usable unless we haven't used them in 6 months
		// or the user has revoked permissions to our developers.google.com application.
		if (googleRefreshToken) {
			return await postObtainAccessTokenFromRefreshToken(
				googleRefreshToken
			);
		}
		// Seems like we've gotta fetch a brand new refresh token and auth token
		// Just so you know, the Auth token provided in here is only valid for about 5 minutes.
		// If you use the refresh token to get an access token, it's valid for waaay longer.
		else if (authCode) {
			try {
				return await fetchCall('/api/google/token', 'POST', {
					authCode: authCode,
					windowLocation: window.location.origin,
				})
					.then((res) => {
						const tempRefreshToken = res?.refresh_token;

						// We can use these temp authorization details while the postObtainAccessTokenFromRefreshToken runs
						dispatch(setGoogleRefreshToken(tempRefreshToken ?? ''));
						dispatch(setGoogleAccessToken(res?.access_token ?? ''));
						dispatch(setIsGoogleFetchingToken(false));

						// We can do this in parallel since we've already got a temp access token.
						return postObtainAccessTokenFromRefreshToken(
							tempRefreshToken
						);
					})
					.catch((err) => {
						// If this call fails, it probably means the auth code for obtaining
						// a refresh token is no good, so we'll clear it out
						router.replace(jwt ? `/?jwt=${jwt}` : '/', undefined, {
							shallow: true,
						});
						logToAi(
							{
								message: `/google/token call threw an error! We'll clear the auth code details so the user can grant us permissions again.`,
								id: 'API_GOOGLE_TOKEN_ERROR',
								location:
									'GooglePhotosButton.jsx:refreshGoogleAccessToken',
								data: {
									error: err,
								},
							},
							'error'
						);
						dispatch(setIsGoogleFetchingToken(false));
						return false;
					});
			} catch (err) {
				logToAi(
					{
						message: `Unexpected error with /google/token API request!`,
						id: 'API_GOOGLE_TOKEN_UNEXPECTED_ERROR',
						location:
							'GooglePhotosButton.jsx:refreshGoogleAccessToken',
						data: {
							error: err,
						},
					},
					'error'
				);
				dispatch(setIsGoogleFetchingToken(false));
				return false;
			}
		}
	}

	/**
	 * Gets a fresh access token by using the refresh token argument
	 * @param {string} refreshToken Refresh token we'll use to get the new access token
	 * @returns True if the refresh token was able to refresh the access token or false if it was unable to
	 */
	async function postObtainAccessTokenFromRefreshToken(refreshToken) {
		try {
			dispatch(setIsGoogleFetchingToken(true));

			return await fetchCall('/api/google/refresh', 'POST', {
				refreshToken: refreshToken,
			})
				.then((res) => {
					dispatch(setGoogleAccessToken(res?.access_token ?? ''));
					dispatch(setIsGoogleFetchingToken(false));
					return true;
				})
				.catch((err) => {
					// If this call fails, it probably means the refresh token is no good, so we'll clear it out
					dispatch(setGoogleRefreshToken(''));
					dispatch(setIsGoogleFetchingToken(false));
					logToAi(
						{
							message: `/api/google/refresh call threw an error! We'll clear the refresh token so we can get a new one.`,
							id: 'API_GOOGLE_REFRESH_ERROR',
							location:
								'GooglePhotosButton.jsx:postObtainAccessTokenFromRefreshToken',
							data: {
								error: err,
							},
						},
						'error'
					);

					return false;
				});
		} catch (err) {
			dispatch(setGoogleRefreshToken(''));
			dispatch(setIsGoogleFetchingToken(false));
			logToAi(
				{
					message: `Unexpected error with /google/refresh API request! Removing the refresh token.`,
					id: 'API_GOOGLE_REFRESH_UNEXPECTED_ERROR',
					location:
						'GooglePhotosButton.jsx:postObtainAccessTokenFromRefreshToken',
					data: {
						error: err,
					},
				},
				'error'
			);
			return false;
		}
	}
	//#endregion
	//#region Render UI
	return (
		<div className={GooglePhotosStyle['google-photos-button-container']}>
			<TertiaryButton
				width={'100%'}
				height={'56px'}
				className="google-photos-auth-button"
				loading={isGoogleFetchingToken}
				disabled={isLoading}
				onClick={goToGooglePhotos}
				onKeyPress={(event) => {
					event.preventDefault();
					if (event.keyCode === 0) {
						goToGooglePhotos();
					}
				}}
			>
				{isGoogleFetchingToken ? null : (
					<>
						<img
							src="/images/googlePhotos.svg"
							alt="Google Photos Icon"
							className="google-photos-icon"
						/>
						<FormattedMessage id={'google-photos-button'} />
					</>
				)}
			</TertiaryButton>
			{/* This is an anchor that we can .click() on mobile to open things in a new tab. */}
			<a
				target="_blank"
				ref={anchorRef}
				style={{ height: '1px', opacity: '0.01', cursor: 'pointer' }}
				href={redirectUrl}
			>
				This should be hidden
			</a>
		</div>
	);
};
//#endregion
GooglePhotosButton.displayName = 'GooglePhotosButton';
export default GooglePhotosButton;
